import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  Button,
  IconButton,
  Divider,
  TextField,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, FilterAlt, Clear } from '@mui/icons-material';
import { useState } from 'react';
import { filterOptions } from './posData';

const TransactionFilters = ({ open, onClose, filters, onApplyFilters, onClearFilters }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  
  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleChange = (field, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (field, value) => {
    setLocalFilters((prev) => {
      const currentValues = prev[field] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      return {
        ...prev,
        [field]: newValues,
      };
    });
  };

  const handleApply = () => {
    onApplyFilters(localFilters);
    onClose();
  };

  const handleClear = () => {
    const clearedFilters = {
      tipoPago: [],
      tipoTransaccion: [],
      empleados: [],
      clientes: [],
      proveedores: [],
      concepto: '',
      fechaInicio: '',
      fechaFin: '',
    };
    setLocalFilters(clearedFilters);
    onClearFilters();
  };

  const hasActiveFilters = Object.values(localFilters).some(
    (value) => (Array.isArray(value) ? value.length > 0 : value)
  );

  // Estilos para full-screen en móvil
  const getPaperStyles = () => {
    if (isMobile) {
      return {
        position: 'fixed',
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        m: 0,
        borderRadius: 0,
      };
    }
    return {
      position: 'fixed',
      right: 0,
      top: 0,
      height: '100%',
      m: 0,
      borderRadius: 0,
      maxWidth: 400,
    };
  };

  // Botones de acción grandes para touch
  const actionButtonStyles = isMobile ? {
    minHeight: 56,
    fontSize: '1rem',
    py: 1.5,
  } : {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: getPaperStyles(),
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        pb: 1,
        // Header más alto en móvil para touch
        minHeight: isMobile ? 64 : 'auto',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterAlt color="primary" />
          <Typography variant="h6">Filtros</Typography>
        </Box>
        <IconButton 
          onClick={onClose} 
          size={isMobile ? "medium" : "small"}
          sx={{ 
            // Touch target grande
            minWidth: 48, 
            minHeight: 48,
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent 
        sx={{ 
          p: 2,
          // Scroll independiente
          overflow: 'auto',
          // En móvil, ocupar todo el espacio disponible
          ...(isMobile && {
            height: 'calc(100% - 140px)', // Restar header y footer
          }),
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Tipo de Pago
          </Typography>
          <FormGroup row={!isMobile}>
            {filterOptions.tipoPago.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    size={isMobile ? "medium" : "small"}
                    checked={localFilters.tipoPago?.includes(option.value) || false}
                    onChange={() => handleCheckboxChange('tipoPago', option.value)}
                    sx={isMobile ? { p: 1 } : {}}
                  />
                }
                label={<Typography variant="body2">{option.label}</Typography>}
                sx={isMobile ? { minHeight: 48, width: '100%', mr: 0 } : {}}
              />
            ))}
          </FormGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Tipo de Transacción
          </Typography>
          <FormGroup row={!isMobile}>
            {filterOptions.tipoTransaccion.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    size={isMobile ? "medium" : "small"}
                    checked={localFilters.tipoTransaccion?.includes(option.value) || false}
                    onChange={() => handleCheckboxChange('tipoTransaccion', option.value)}
                    sx={isMobile ? { p: 1 } : {}}
                  />
                }
                label={<Typography variant="body2">{option.label}</Typography>}
                sx={isMobile ? { minHeight: 48, width: '100%', mr: 0 } : {}}
              />
            ))}
          </FormGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Empleado
          </Typography>
          <FormControl fullWidth size={isMobile ? "medium" : "small"}>
            <InputLabel>Todos los empleados</InputLabel>
            <Select
              multiple
              value={localFilters.empleados || []}
              onChange={(e) => handleChange('empleados', e.target.value)}
              label="Todos los empleados"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const option = filterOptions.empleados.find((o) => o.value === value);
                    return <Chip key={value} label={option?.label || value} size="small" />;
                  })}
                </Box>
              )}
            >
              {filterOptions.empleados.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ minHeight: isMobile ? 48 : 'auto' }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Cliente
          </Typography>
          <FormControl fullWidth size={isMobile ? "medium" : "small"}>
            <InputLabel>Todos los clientes</InputLabel>
            <Select
              multiple
              value={localFilters.clientes || []}
              onChange={(e) => handleChange('clientes', e.target.value)}
              label="Todos los clientes"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const option = filterOptions.clientes.find((o) => o.value === value);
                    return <Chip key={value} label={option?.label || value} size="small" />;
                  })}
                </Box>
              )}
            >
              {filterOptions.clientes.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ minHeight: isMobile ? 48 : 'auto' }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Proveedor
          </Typography>
          <FormControl fullWidth size={isMobile ? "medium" : "small"}>
            <InputLabel>Todos los proveedores</InputLabel>
            <Select
              multiple
              value={localFilters.proveedores || []}
              onChange={(e) => handleChange('proveedores', e.target.value)}
              label="Todos los proveedores"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const option = filterOptions.proveedores.find((o) => o.value === value);
                    return <Chip key={value} label={option?.label || value} size="small" />;
                  })}
                </Box>
              )}
            >
              {filterOptions.proveedores.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ minHeight: isMobile ? 48 : 'auto' }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Concepto
          </Typography>
          <TextField
            fullWidth
            size={isMobile ? "medium" : "small"}
            placeholder="Buscar en concepto..."
            value={localFilters.concepto || ''}
            onChange={(e) => handleChange('concepto', e.target.value)}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Rango de Fechas
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
            <TextField
              fullWidth
              size={isMobile ? "medium" : "small"}
              type="date"
              label="Desde"
              value={localFilters.fechaInicio || ''}
              onChange={(e) => handleChange('fechaInicio', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              size={isMobile ? "medium" : "small"}
              type="date"
              label="Hasta"
              value={localFilters.fechaFin || ''}
              onChange={(e) => handleChange('fechaFin', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      {/* Botones de acción - más grandes en móvil */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        gap: 2, 
        justifyContent: 'flex-end',
        // Footer fijo en móvil
        ...(isMobile && {
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
        }),
      }}>
        <Button 
          variant="outlined" 
          startIcon={<Clear />} 
          onClick={handleClear} 
          disabled={!hasActiveFilters}
          sx={actionButtonStyles}
        >
          Limpiar
        </Button>
        <Button 
          variant="contained" 
          onClick={handleApply}
          sx={actionButtonStyles}
        >
          Aplicar Filtros
        </Button>
      </Box>
    </Dialog>
  );
};

export default TransactionFilters;
